import React, { useEffect, useRef } from 'react';
import { SearchResult } from '@/stores/search';
import { File, FunctionSquare } from 'lucide-react';
import { cn } from "@/lib/utils"

interface InlineSearchProps {
  query: string;
  results: SearchResult[];
  onSelect: (result: SearchResult) => void;
  loading: boolean;
  highlightedIndex: number;
  className?: string;
}

export const InlineSearch: React.FC<InlineSearchProps> = ({ query, results, onSelect, loading, highlightedIndex, className }) => {
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const isDraggingRef = useRef(false);

  useEffect(() => {
    itemRefs.current = itemRefs.current.slice(0, results.length);
  }, [results]);

  useEffect(() => {
    if (itemRefs.current[highlightedIndex]) {
      itemRefs.current[highlightedIndex]!.scrollIntoView({
        block: 'nearest',
      });
    }
  }, [highlightedIndex]);

  if (loading) {
    return <div className="p-4 text-center text-sm text-muted-foreground">Loading...</div>;
  }

  if (results.length === 0 && query.length > 0) {
    return <div className="p-4 text-center text-sm text-muted-foreground">No results found for "{query}"</div>;
  }

  return (
    <div
      className={cn("max-h-[300px] overflow-y-auto p-1", className)}
      onMouseDown={(e) => {
        // Prevent the mousedown event from causing the textarea to blur.
        e.preventDefault();
      }}
    >
      {results.map((result, index) => (
        <div
        data-index={index}
          key={`${result.path}-${result.name}-${index}`}
          ref={(el) => (itemRefs.current[index] = el)}
          onTouchStart={(e) => {
                isDraggingRef.current = false;
                touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
          }}
          onTouchMove={(e) => {
                if (touchStartRef.current) {
                      const dx = Math.abs(e.touches[0].clientX - touchStartRef.current.x);
                      const dy = Math.abs(e.touches[0].clientY - touchStartRef.current.y);
                      // If the movement is more than a small threshold (e.g., 10px), consider it a drag/scroll
                      if (dx > 10 || dy > 10) {
                            isDraggingRef.current = true;
                      }
                }
          }}
          onTouchEnd={(e) => {
            e.preventDefault();
            e.stopPropagation();
            // Only select if it wasn't a drag/scroll.
            if (!isDraggingRef.current) {
              // To solve virtual keyboard issues, we use `elementFromPoint` with the final
              // touch coordinates. This correctly identifies the element under the user's
              // finger in the VISUAL viewport, ignoring layout shifts.
              const touch = e.changedTouches[0];
              const targetElement = document.elementFromPoint(touch.clientX, touch.clientY);

              if (targetElement) {
                // The tap might be on a child (e.g., span, svg). Find the parent with our data attribute.
                const resultElement = targetElement.closest('[data-index]') as HTMLDivElement | null;
                if (resultElement && resultElement.dataset.index) {
                  const tappedIndex = parseInt(resultElement.dataset.index, 10);
                  if (!isNaN(tappedIndex) && results[tappedIndex]) {
                    onSelect(results[tappedIndex]);
                  }
                }
              }
            }
          }}
          onClick={(_e) => {
            // Only for non-touch devices
            if (!('ontouchstart' in window)) {
              onSelect(result);
            }
          }}
          className={cn(
            "relative flex cursor-pointer gap-2 select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none",
            index === highlightedIndex && "bg-accent text-accent-foreground"
          )}
        >
          {result.type === 'symbol' ? <FunctionSquare className="mr-2 h-4 w-4 shrink-0" /> : <File className="mr-2 h-4 w-4 shrink-0" />}
          <div className="flex flex-col overflow-hidden">
            <span className="font-medium truncate">{result.name || result.path}</span>
            {result.name && result.path && (
              <span className="text-xs text-muted-foreground truncate">{result.path}</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};